// ============================================================================
// ACCEPT INVITE VIEW — Invite acceptance + account creation
//
// Route: /accept-invite?inviteId={inviteId}
//
// Flow:
//   1. Read invite doc (unauthenticated read allowed by rules)
//   2. Validate status=pending, not expired, not revoked, not claimed
//   3. Collect full name + password from invited user
//   4. Create Firebase Auth account
//   5. Call claimInvite CF to provision user/member docs
//   6. Redirect to default landing for role
// ============================================================================

import React, { useState, useEffect } from 'react';
import { safeUserError } from '../core/safeError.js';
import { Shield, Loader2, AlertCircle, Eye, EyeOff } from 'lucide-react';
import { getInvite } from '../core/index.js';
import { claimInviteCF } from '../core/functions.js';
import { defaultRouteForRole, ROLE_DISPLAY } from '../core/rbac.js';
import {
  getAuth as getFirebaseAuth,
  createUserWithEmailAndPassword,
  updateProfile,
} from 'firebase/auth';
import { getApp } from 'firebase/app';

function getInviteIdFromUrl() {
  const params = new URLSearchParams(window.location.search);
  return params.get('inviteId');
}

function isExpired(invite) {
  if (!invite.expiresAt) return false;
  const expiry = invite.expiresAt.toDate
    ? invite.expiresAt.toDate()
    : new Date((invite.expiresAt.seconds ?? 0) * 1000);
  return expiry.getTime() <= Date.now();
}

// ── Error States ─────────────────────────────────────────────────────────────

function InviteError({ title, message }) {
  return (
    <div className="min-h-screen bg-slate-950 flex items-center justify-center p-4">
      <div className="w-full max-w-md">
        <div className="bg-slate-900 border border-slate-700/50 rounded-2xl p-8 text-center">
          <div className="w-12 h-12 rounded-full bg-red-500/15 flex items-center justify-center mx-auto mb-4">
            <AlertCircle className="w-6 h-6 text-red-400" />
          </div>
          <h1 className="text-lg font-semibold text-white mb-2">{title}</h1>
          <p className="text-sm text-slate-400">{message}</p>
        </div>
      </div>
    </div>
  );
}

// ── Main View ────────────────────────────────────────────────────────────────

export default function AcceptInviteView({ onAccepted }) {
  const [invite, setInvite] = useState(null);
  const [loading, setLoading] = useState(true);
  const [inviteError, setInviteError] = useState(null);

  const [fullName, setFullName] = useState('');
  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [formError, setFormError] = useState('');

  const inviteId = getInviteIdFromUrl();

  useEffect(() => {
    if (!inviteId) {
      setInviteError({ title: 'Invite not found.', message: 'No invite ID was provided in the link.' });
      setLoading(false);
      return;
    }

    (async () => {
      try {
        const inv = await getInvite(inviteId);
        if (!inv) {
          setInviteError({ title: 'Invite not found.', message: 'This invite link is invalid or has been removed.' });
        } else if (inv.status === 'claimed' || inv.claimedAt) {
          setInviteError({ title: 'Invite already used.', message: 'This invite has already been used.' });
        } else if (inv.status === 'revoked' || inv.revokedAt) {
          setInviteError({ title: 'Invite revoked.', message: 'This invite was revoked.' });
        } else if (inv.status !== 'pending') {
          setInviteError({ title: 'Invite unavailable.', message: `This invite is in an invalid state: ${inv.status}` });
        } else if (isExpired(inv)) {
          setInviteError({ title: 'Invite expired.', message: 'This invite has expired. Ask your admin for a new invite.' });
        } else {
          setInvite(inv);
        }
      } catch (err) {
        setInviteError({ title: 'Failed to load invite.', message: safeUserError(err) ?? 'Please try again later.' });
      } finally {
        setLoading(false);
      }
    })();
  }, [inviteId]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setFormError('');

    const trimmedName = fullName.trim();
    if (!trimmedName) {
      setFormError('Enter your full name.');
      return;
    }
    if (!password) {
      setFormError('Enter a password.');
      return;
    }
    if (password.length < 6) {
      setFormError('Password must be at least 6 characters.');
      return;
    }
    if (password !== confirmPassword) {
      setFormError('Passwords do not match.');
      return;
    }

    setSubmitting(true);

    try {
      const auth = getFirebaseAuth(getApp());
      const credential = await createUserWithEmailAndPassword(
        auth,
        invite.recipientEmailLower,
        password,
      );

      await updateProfile(credential.user, { displayName: trimmedName });

      const result = await claimInviteCF(inviteId);

      // Force token refresh so new JWT claims (role, tenantId) take effect
      await credential.user.getIdToken(true);

      if (onAccepted) {
        onAccepted(result.role);
      } else {
        const landing = defaultRouteForRole(result.role);
        window.location.replace(`/${landing}`);
      }
    } catch (err) {
      const code = err?.code || '';
      if (code === 'auth/email-already-in-use') {
        setFormError('An account already exists for this email. Sign in with the invited email or ask your admin for a new invite.');
      } else if (code === 'auth/weak-password') {
        setFormError('Password is too weak. Use at least 6 characters.');
      } else if (code === 'auth/invalid-email') {
        setFormError('The email address is invalid.');
      } else {
        setFormError(safeUserError(err) ?? 'Something went wrong. Please try again.');
      }
      setSubmitting(false);
    }
  };

  // ── Loading ────────────────────────────────────────────────────────────────
  if (loading) {
    return (
      <div className="min-h-screen bg-slate-950 flex items-center justify-center">
        <Loader2 className="w-8 h-8 text-violet-400 animate-spin" />
      </div>
    );
  }

  // ── Error ──────────────────────────────────────────────────────────────────
  if (inviteError) {
    return <InviteError title={inviteError.title} message={inviteError.message} />;
  }

  // ── Form ───────────────────────────────────────────────────────────────────
  const roleName = ROLE_DISPLAY[invite.role]?.name ?? invite.role;
  const orgName = invite.tenantNameSnapshot || invite.tenantId;

  return (
    <div className="min-h-screen bg-slate-950 flex items-center justify-center p-4">
      <div className="w-full max-w-md">
        <div className="bg-slate-900 border border-slate-700/50 rounded-2xl overflow-hidden">
          {/* Header */}
          <div className="p-6 border-b border-slate-700/50 text-center">
            <div className="w-10 h-10 rounded-xl bg-violet-500/15 flex items-center justify-center mx-auto mb-3">
              <Shield className="w-5 h-5 text-violet-400" />
            </div>
            <h1 className="text-lg font-semibold text-white">Join {orgName}</h1>
            <p className="text-sm text-slate-400 mt-1">Create your account to accept this invite.</p>
          </div>

          {/* Invite details */}
          <div className="px-6 pt-5">
            <div className="bg-slate-800/50 border border-slate-700/30 rounded-xl p-4 space-y-2">
              <div className="flex justify-between text-sm">
                <span className="text-slate-400">Organization</span>
                <span className="text-white font-medium">{orgName}</span>
              </div>
              <div className="flex justify-between text-sm">
                <span className="text-slate-400">Role</span>
                <span className="text-white font-medium">{roleName}</span>
              </div>
              <div className="flex justify-between text-sm">
                <span className="text-slate-400">Email</span>
                <span className="text-white font-medium">{invite.recipientEmailRaw || invite.recipientEmailLower}</span>
              </div>
            </div>
          </div>

          {/* Form */}
          <form onSubmit={handleSubmit} className="p-6 space-y-4">
            {/* Full Name */}
            <div>
              <label className="block text-sm font-medium text-slate-300 mb-1.5">Full Name</label>
              <input
                type="text"
                value={fullName}
                onChange={(e) => setFullName(e.target.value)}
                placeholder="Dr. Jane Smith"
                className="w-full px-4 py-2.5 rounded-xl bg-slate-800 border border-slate-700 text-white placeholder-slate-500 focus:outline-none focus:ring-2 focus:ring-violet-500/50 focus:border-violet-500"
                disabled={submitting}
                autoFocus
              />
            </div>

            {/* Password */}
            <div>
              <label className="block text-sm font-medium text-slate-300 mb-1.5">Password</label>
              <div className="relative">
                <input
                  type={showPassword ? 'text' : 'password'}
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  placeholder="Min. 6 characters"
                  className="w-full px-4 py-2.5 rounded-xl bg-slate-800 border border-slate-700 text-white placeholder-slate-500 focus:outline-none focus:ring-2 focus:ring-violet-500/50 focus:border-violet-500 pr-11"
                  disabled={submitting}
                />
                <button
                  type="button"
                  onClick={() => setShowPassword((v) => !v)}
                  className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-400 hover:text-white"
                  tabIndex={-1}
                >
                  {showPassword ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                </button>
              </div>
            </div>

            {/* Confirm Password */}
            <div>
              <label className="block text-sm font-medium text-slate-300 mb-1.5">Confirm Password</label>
              <input
                type={showPassword ? 'text' : 'password'}
                value={confirmPassword}
                onChange={(e) => setConfirmPassword(e.target.value)}
                placeholder="Re-enter password"
                className="w-full px-4 py-2.5 rounded-xl bg-slate-800 border border-slate-700 text-white placeholder-slate-500 focus:outline-none focus:ring-2 focus:ring-violet-500/50 focus:border-violet-500"
                disabled={submitting}
              />
            </div>

            {/* Error */}
            {formError && (
              <div className="flex items-start gap-2 p-3 rounded-lg bg-red-500/10 border border-red-500/20">
                <AlertCircle className="w-4 h-4 text-red-400 mt-0.5 flex-shrink-0" />
                <p className="text-sm text-red-400">{formError}</p>
              </div>
            )}

            {/* Submit */}
            <button
              type="submit"
              disabled={submitting}
              className="w-full px-4 py-3 rounded-xl bg-violet-600 hover:bg-violet-500 text-white font-semibold transition-all disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-2"
            >
              {submitting ? (
                <>
                  <Loader2 className="w-4 h-4 animate-spin" />
                  Creating account…
                </>
              ) : (
                'Create account & join'
              )}
            </button>
          </form>
        </div>

        <p className="text-center text-xs text-slate-500 mt-4">
          Already have an account?{' '}
          <a href="/" className="text-violet-400 hover:text-violet-300 underline">
            Sign in
          </a>
        </p>
      </div>
    </div>
  );
}
